import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TOKENS_RENEW, EXTERNAL_IPIFY_URL } from '@/app/constants/apiConstants';
import type { Store } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { translate } from './i18nHelper';
import { logger } from './logger';
import { authErrorHandler } from './authErrorHandler';
import { tokenManager } from '@/app/services/auth/tokenManager';
import { getClientTimezone } from './clientTimezone';

// ---------------------------------------------------------------------------
// Store injection — apiClient no longer imports `store` at runtime to avoid a
// circular dependency that breaks Jest module-resolution (apiClient → store →
// slices → thunks → services → apiClient). The store calls `setApiClientStore`
// after `configureStore` returns; until then, getState() throws and any code
// that triggers an API call without a Provider in place fails loud.
// ---------------------------------------------------------------------------
let _apiClientStore: Store<RootState> | null = null;
export function setApiClientStore(store: Store<RootState>): void {
  _apiClientStore = store;
}
function getStoreState(): RootState {
  if (!_apiClientStore) {
    throw new Error(
      'apiClient: store not initialized. Ensure setApiClientStore(store) is called from app/store/index.ts.'
    );
  }
  return _apiClientStore.getState();
}

// ---------------------------------------------------------------------------
// Public IP resolution — fetched once from the browser, cached in memory
// and localStorage for persistence across page loads.
// Used to send X-Client-IP header so the backend can capture the real public
// IP in local dev (where there is no reverse proxy injecting X-Forwarded-For).
// Only runs client-side (browser); skipped during SSR.
// ---------------------------------------------------------------------------
const PUBLIC_IP_CACHE_KEY = 'sm_public_ip';
let cachedPublicIp: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;

async function getPublicIp(): Promise<string | null> {
  // Skip on server-side rendering
  if (typeof window === 'undefined') return null;

  // Return in-memory cache first
  if (cachedPublicIp) return cachedPublicIp;

  // Try localStorage (survives page reload — avoids race on first request)
  try {
    const stored = localStorage.getItem(PUBLIC_IP_CACHE_KEY);
    if (stored) {
      cachedPublicIp = stored;
      return cachedPublicIp;
    }
  } catch { /* ignore */ }

  // Deduplicate concurrent calls
  if (ipFetchPromise) return ipFetchPromise;

  ipFetchPromise = Promise.race([
    fetch(EXTERNAL_IPIFY_URL)
      .then(res => res.json())
      .then((data: { ip?: string }) => {
        cachedPublicIp = data.ip ?? null;
        if (cachedPublicIp) {
          try { localStorage.setItem(PUBLIC_IP_CACHE_KEY, cachedPublicIp); } catch { /* ignore */ }
        }
        return cachedPublicIp;
      }),
    // 3-second timeout — if ipify is slow/down, don't block API calls
    new Promise<null>(resolve => setTimeout(() => resolve(null), 3000))
  ]).catch(() => null); // silently ignore any error — IP is non-critical

  return ipFetchPromise;
}

// Kick off the fetch immediately on the client so it's ready before the first API call
if (typeof window !== 'undefined') {
  getPublicIp();
}

/**
 * Navigate to the login page after auth failure. Guards against running on the
 * server, redirecting when already on /login, or firing more than once while a
 * navigation is in-flight (multiple concurrent requests may all hit this path
 * when a refresh fails).
 */
let isRedirectingToLogin = false;
function redirectToLoginIfNeeded(): void {
  if (typeof window === 'undefined') return;
  if (isRedirectingToLogin) return;
  if (window.location.pathname === '/login') return;
  isRedirectingToLogin = true;
  window.location.href = '/login';
}

/**
 * API Error with user-friendly messages
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: any,
    public isNetworkError: boolean = false,
    public isTimeout: boolean = false
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryableStatuses: number[];
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryableStatuses: [408, 429, 500, 502, 503, 504], // Timeout, Rate limit, Server errors
};

/**
 * Create configured axios instance
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000, // 60 seconds — bumped from 30s 2026-05-17 because the
                    // packaging-rule supersede write-path can take 25-30s on
                    // AAD cold-start. Permanent fix is the AAD-token-caching
                    // SqlAuthenticationProvider (in flight). Once it lands and
                    // typical supersede is <2s, this can drop back to 30s.
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token with pre-flight validation and automatic refresh
  client.interceptors.request.use(
    async (config) => {
      const state = getStoreState();
      
      // Get token from unified auth system
      const isAuthenticated = state.auth.isAuthenticated;
      const user = state.auth.user;
      
      // Get token from tokenManager (centralized token access)
      let tokens = tokenManager.getTokens();
      let accessToken = tokens?.accessToken || null;

      // Skip pre-flight refresh for the token renewal endpoint itself.
      // performTokenRefresh() calls apiClient.post(API_TOKENS_RENEW, ...) — if we
      // recurse into refresh logic here, the nested call awaits the pending
      // refreshPromise that is waiting for this very POST, causing a deadlock
      // that hangs the page indefinitely when the access token has expired.
      const isRefreshRequest = !!config.url && config.url.includes(API_TOKENS_RENEW);

      // PRE-FLIGHT TOKEN VALIDATION AND REFRESH
      if (!isRefreshRequest && tokens && tokens.expiresAt) {
        const now = Date.now();
        const timeUntilExpiry = tokens.expiresAt - now;
        const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);

        // Check if token is expired or expires soon (within 5 minutes)
        if (timeUntilExpiry < 5 * 60 * 1000) {
          const isExpired = timeUntilExpiry < 0;
          const minutesExpired = Math.abs(minutesUntilExpiry);
          
          if (isExpired) {
            logger.warn('API Request: Token expired, attempting automatic refresh', {
              url: config.url,
              method: config.method?.toUpperCase(),
              expiredSince: `${minutesExpired} minute${minutesExpired !== 1 ? 's' : ''} ago`,
              expiresAt: new Date(tokens.expiresAt).toISOString()
            });
          } else {
            logger.warn('API Request: Token expires soon, attempting proactive refresh', {
              url: config.url,
              method: config.method?.toUpperCase(),
              expiresIn: `${minutesUntilExpiry} minute${minutesUntilExpiry !== 1 ? 's' : ''}`,
              expiresAt: new Date(tokens.expiresAt).toISOString()
            });
          }

          // Attempt to refresh the token
          try {
            const refreshSuccess = await tokenManager.refreshTokensIfNeeded();
            
            if (refreshSuccess) {
              // Get the new token after refresh
              tokens = tokenManager.getTokens();
              accessToken = tokens?.accessToken || null;
              
              logger.auth('Token refreshed successfully before API request', {
                url: config.url,
                method: config.method?.toUpperCase(),
                newTokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null,
                newExpiry: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null
              });
            } else {
              // Refresh failed — clear tokens, redirect to login, reject the request.
              // Without the redirect, NotificationBell (and other polling UIs) keep
              // firing requests that hang on this path and the page shows a spinner
              // forever. initializeSession only runs at bootstrap, so runtime expiry
              // must redirect here.
              logger.error('Token refresh failed, redirecting to login', {
                url: config.url,
                method: config.method?.toUpperCase()
              });

              tokenManager.clearTokens();
              redirectToLoginIfNeeded();

              return Promise.reject(new ApiError(
                'Your session has expired. Please log in again.',
                401,
                undefined,
                false,
                false
              ));
            }
          } catch (refreshError) {
            logger.error('Token refresh error, redirecting to login', refreshError);

            tokenManager.clearTokens();
            redirectToLoginIfNeeded();

            return Promise.reject(new ApiError(
              'Your session has expired. Please log in again.',
              401,
              refreshError,
              false,
              false
            ));
          }
        }
      }

      // Debug logging
      logger.apiCall(config.method?.toUpperCase() || 'UNKNOWN', config.url || '', {
        hasToken: !!accessToken,
        isAuthenticated,
        authSource: 'tokenManager',
        userId: user?.id,
        tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null,
        tokenLength: accessToken?.length || 0,
        tokenExpiry: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
        tokenValid: tokens ? tokenManager.isAccessTokenValid() : false,
        fullUrl: `${config.baseURL || ''}${config.url || ''}`,
        headers: config.headers ? Object.keys(config.headers) : []
      });

      // Inject X-Tenant-Id header if an active tenant is selected
      const activeTenant = getStoreState().tenant?.activeTenant;
      if (activeTenant) {
        config.headers['X-Tenant-Id'] = activeTenant.tenantId;
      }

      // Inject X-Client-IP for local dev (backend uses this when no proxy header is present)
      const publicIp = await getPublicIp();
      if (publicIp) {
        config.headers['X-Client-IP'] = publicIp;
      }

      // Spec F (chat-proactive-greeting), Phase 12 — attach the browser's
      // IANA timezone to every request. The chat greeting producer (Phase 5)
      // and the `get_my_session_info` tool (Phase 7.5) both read this header
      // to compute time-of-day and local wall-clock. The value is cached at
      // module load and skipped during SSR.
      const clientTimezone = getClientTimezone();
      if (clientTimezone) {
        config.headers['X-Client-Timezone'] = clientTimezone;
      }

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        
        const timeUntilExpiry = tokens?.expiresAt ? tokens.expiresAt - Date.now() : 0;
        const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);
        
        logger.debug('API Client: Added Authorization header from tokenManager', {
          url: config.url,
          method: config.method?.toUpperCase(),
          tokenPreview: `${accessToken.substring(0, 20)}...`,
          authHeaderSet: true,
          expiresAt: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
          expiresIn: minutesUntilExpiry > 0 ? `${minutesUntilExpiry} minute${minutesUntilExpiry !== 1 ? 's' : ''}` : 'expired'
        });
      } else {
        logger.warn('API Client: No access token available for request', { 
          url: config.url,
          method: config.method?.toUpperCase(),
          isAuthenticated,
          authSource: 'tokenManager',
          hasTokens: !!tokens
        });
      }

      return config;
    },
    (error) => {
      logger.error('API Client: Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle errors with enhanced auth handling
  client.interceptors.response.use(
    (response) => {
      // Log successful responses for debugging
      logger.debug('API Client: Successful response', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        hasData: !!response.data
      });
      return response;
    },
    async (error: AxiosError) => {
      // Expected user-flow rejections (e.g. chat user memory redaction reject)
      // should not noisily log at error level.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bodyCode = (error.response?.data as any)?.code;
      const isExpectedRejection =
        error.response?.status === 400 && bodyCode === 'memory_redacted_reject';

      // User clicked Cancel on an in-flight request (e.g. ASN ingest cancel).
      // Axios throws CanceledError when AbortController.abort() fires; that's
      // not a server failure, so don't surface it via the dev error overlay.
      const isUserCancelled = axios.isCancel(error);

      const logMeta = {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        hasAuthHeader: !!(error.config?.headers?.Authorization),
        authHeaderPreview: error.config?.headers?.Authorization
          ? `${String(error.config.headers.Authorization).substring(0, 30)}...`
          : null,
        bodyCode
      };

      if (isUserCancelled) {
        logger.debug('API Client: Request cancelled by user', logMeta);
        return Promise.reject(error);
      }

      if (isExpectedRejection) {
        logger.debug('API Client: Expected rejection', logMeta);
      } else {
        logger.error('API Client: Request failed', error, logMeta);
      }

      // Handle 401 errors with automatic token refresh
      if (error.response?.status === 401 && error.config) {
        try {
          return await authErrorHandler.handle401Error(error, error.config);
        } catch (authError) {
          // If auth handling fails, continue with normal error handling
          logger.warn('Auth error handler failed, falling back to normal error handling', authError);
        }
      }

      return Promise.reject(await handleApiError(error));
    }
  );

  return client;
};

/**
 * Handle API errors and convert to user-friendly messages
 */
async function handleApiError(error: AxiosError): Promise<ApiError> {
  // Network error (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new ApiError(
        translate('errors.api.requestTimeout'),
        undefined,
        error,
        false,
        true
      );
    }

    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      return new ApiError(
        translate('errors.api.networkFailed'),
        undefined,
        error,
        true,
        false
      );
    }

    return new ApiError(
      translate('errors.api.unableToConnect'),
      undefined,
      error,
      true,
      false
    );
  }

  // HTTP error responses
  const status = error.response.status;
  const data: any = error.response.data;

  switch (status) {
    case 400: {
      // Surface the real validation detail across the 3 shapes the BE emits:
      //  - ApiResult / MediatR ValidationBehavior → { messages: [...] }
      //  - FluentValidation auto-validation / ValidationProblemDetails → { errors: { field: [msg] } }
      //  - custom ValidationActionFilter → { extensions: { errors: [{ field, message }] } }
      const fromMessages = Array.isArray(data?.messages) ? data.messages : null;
      const fromProblem =
        data?.errors && typeof data.errors === 'object'
          ? Object.values(data.errors as Record<string, string[] | string>).flat()
          : null;
      const fromExtensions = Array.isArray(data?.extensions?.errors)
        ? (data.extensions.errors as Array<{ message?: string }>).map(
            (e) => e?.message ?? String(e)
          )
        : null;
      const detail = (fromMessages ?? fromProblem ?? fromExtensions ?? [])
        .filter(Boolean)
        .join(', ');
      return new ApiError(
        detail || data?.detail || translate('errors.api.badRequest'),
        status,
        error
      );
    }

    case 401:
      return new ApiError(
        translate('errors.api.sessionExpired'),
        status,
        error
      );

    case 403:
      return new ApiError(
        translate('errors.api.noPermission'),
        status,
        error
      );

    case 404:
      return new ApiError(
        translate('errors.api.notFound'),
        status,
        error
      );

    case 408:
      return new ApiError(
        translate('errors.api.requestTimeout'),
        status,
        error,
        false,
        true
      );

    case 429:
      return new ApiError(
        translate('errors.api.tooManyRequests'),
        status,
        error
      );

    case 500:
      return new ApiError(
        translate('errors.api.serverError'),
        status,
        error
      );

    case 502:
    case 503:
      return new ApiError(
        translate('errors.api.serviceUnavailable'),
        status,
        error
      );

    case 504:
      return new ApiError(
        translate('errors.api.gatewayTimeout'),
        status,
        error,
        false,
        true
      );

    default:
      return new ApiError(
        data?.messages?.join(', ') || translate('errors.api.unexpectedError', { status }),
        status,
        error
      );
  }
}

/**
 * Retry logic for failed requests
 */
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const retryConfig = { ...defaultRetryConfig, ...config };
  let lastError: ApiError | undefined;

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as ApiError;

      // Don't retry if it's not a retryable error
      if (
        !lastError.isNetworkError &&
        !lastError.isTimeout &&
        lastError.statusCode &&
        !retryConfig.retryableStatuses.includes(lastError.statusCode)
      ) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === retryConfig.maxRetries) {
        throw lastError;
      }

      // Wait before retrying (exponential backoff)
      const delay = retryConfig.retryDelay * Math.pow(2, attempt);
      logger.performance(`Retrying request in ${delay}ms (attempt ${attempt + 1}/${retryConfig.maxRetries})...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * API Client instance
 */
export const apiClient = createApiClient();

/**
 * API Client with retry
 */
export const apiClientWithRetry = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    retryRequest(() => apiClient.get<T>(url, config).then((res) => res.data)),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    retryRequest(() => apiClient.post<T>(url, data, config).then((res) => res.data)),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    retryRequest(() => apiClient.put<T>(url, data, config).then((res) => res.data)),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    retryRequest(() => apiClient.delete<T>(url, config).then((res) => res.data)),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    retryRequest(() => apiClient.patch<T>(url, data, config).then((res) => res.data)),
};

/**
 * Check if error is an API error
 */
export function isApiError(error: any): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: any): string {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}
